/**
 * UnitWallCollisionBuilder
 * Creates wall collision meshes from UnitCollisionConfig (edge-based)
 * Supports door openings via solid wall segments
 */

import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { FEET_TO_METERS } from '@/constants/units'
import { ENABLE_COLLISION_DEBUG, DEBUG_WALL_COLLISIONS } from '@/constants/collision'
import type { UnitCollisionConfig, WallSegment } from '@/types/collision'
import { createArchCollisionMeshes } from './ArchWallCollisionBuilder'

// Debug logging state
let collisionDebugEnabled = ENABLE_COLLISION_DEBUG

/**
 * Enable unit wall collision debug logging
 */
export function enableUnitWallDebug(): void {
  collisionDebugEnabled = true
}

/**
 * Disable unit wall collision debug logging
 */
export function disableUnitWallDebug(): void {
  collisionDebugEnabled = false
}

/**
 * Debug logging utility for unit wall collisions
 */
function debugLog(message: string, data?: unknown): void {
  if (!collisionDebugEnabled || !DEBUG_WALL_COLLISIONS) return

  if (data) {
    console.log(`🏗️ [UNIT WALL] ${message}`, data)
  } else {
    console.log(`🏗️ [UNIT WALL] ${message}`)
  }
}

/**
 * Result of unit wall mesh creation
 */
export interface UnitWallCollisionResult {
  /** OBBs for broad-phase collision detection */
  obbs: OBB[]
  /** Meshes with BVH for narrow-phase detection */
  meshes: THREE.Mesh[]
  /** Unit ID this wall set belongs to */
  unitId: string
  /** Area ID (building) this unit belongs to */
  areaId: string
  /** Which edge each OBB/mesh represents */
  edgeSides: Array<'front' | 'back' | 'left' | 'right'>
  /** Whether each OBB/mesh is a door (true) or solid wall (false) */
  isDoorFlags: boolean[]
}

/**
 * Calculate wall position offset based on edge side
 * Returns [x, z] offset from unit center (in feet)
 */
function getWallOffset(
  side: 'front' | 'back' | 'left' | 'right',
  unitWidth: number,
  unitDepth: number,
  wallThickness: number
): [number, number] {
  const halfWidth = unitWidth / 2
  const halfDepth = unitDepth / 2
  const halfThickness = wallThickness / 2

  switch (side) {
    case 'front':
      // Front wall is at positive Z (toward hangar door)
      return [0, halfDepth + halfThickness]
    case 'back':
      // Back wall is at negative Z
      return [0, -halfDepth - halfThickness]
    case 'left':
      // Left wall at +X (east) — matches WallRenderer swap convention
      return [halfWidth + halfThickness, 0]
    case 'right':
      // Right wall at -X (west) — matches WallRenderer swap convention
      return [-halfWidth - halfThickness, 0]
  }
}

/**
 * Calculate wall dimensions for a solid segment
 * Returns [width, height, depth] in feet
 */
function getSegmentDimensions(
  side: 'front' | 'back' | 'left' | 'right',
  segmentLength: number,
  wallHeight: number,
  wallThickness: number
): [number, number, number] {
  // Front/back walls run along X axis
  // Left/right walls run along Z axis
  if (side === 'front' || side === 'back') {
    return [segmentLength, wallHeight, wallThickness]
  } else {
    return [wallThickness, wallHeight, segmentLength]
  }
}

/**
 * Calculate segment center offset along the wall
 * Returns offset from wall center in the wall's length direction (feet)
 */
function getSegmentOffset(
  side: 'front' | 'back' | 'left' | 'right',
  segment: WallSegment,
  wallLength: number
): [number, number] {
  // Segment center position along wall
  const segmentCenter = (segment.start_ft + segment.end_ft) / 2
  // Offset from wall center (wall center = wallLength / 2)
  const offsetAlongWall = segmentCenter - wallLength / 2

  // Front/back walls: offset is in X direction
  // Left/right walls: offset is in Z direction
  if (side === 'front' || side === 'back') {
    return [offsetAlongWall, 0]
  } else {
    // Negate Z to match WallRenderer PI/2 rotation convention
    // (local X maps to -Z in world space after rotation)
    return [0, -offsetAlongWall]
  }
}

/**
 * Create wall collision meshes for a single unit
 */
export function createUnitWallMeshes(config: UnitCollisionConfig): UnitWallCollisionResult {
  // Arch hangars use curved geometry instead of flat boxes
  if (config.archConfig) {
    return createArchCollisionMeshes(config)
  }

  const {
    unitId,
    areaId,
    position,
    dimensions,
    wallThickness,
    rotation,
    edges,
  } = config

  const [unitWidth, unitDepth] = dimensions

  const obbs: OBB[] = []
  const meshes: THREE.Mesh[] = []
  const edgeSides: Array<'front' | 'back' | 'left' | 'right'> = []
  const isDoorFlags: boolean[] = []

  // Unit position is already in meters
  const unitPos = new THREE.Vector3(position[0], position[1], position[2])
  const rotationRad = (rotation * Math.PI) / 180
  const hasRotation = rotation !== 0

  debugLog(`Creating walls for unit ${unitId} (area: ${areaId})`, {
    position: position.map(p => p.toFixed(2)),
    dimensions,
    rotation,
    edgeCount: edges.length,
  })

  for (const edge of edges) {
    // Skip internal edges (open bays between units)
    if (edge.type === 'internal') {
      debugLog(`Skipping internal edge: ${edge.side}`)
      continue
    }

    // Skip edges with no solid segments (fully open, like front door)
    if (edge.solidSegments.length === 0) {
      debugLog(`Skipping edge with no solid segments: ${edge.side}`)
      continue
    }

    // Get base wall offset from unit center
    const [baseOffsetX, baseOffsetZ] = getWallOffset(
      edge.side,
      unitWidth,
      unitDepth,
      wallThickness
    )

    // Create a wall mesh for each solid segment
    for (const segment of edge.solidSegments) {
      const segmentLength = segment.end_ft - segment.start_ft
      if (segmentLength <= 0) continue

      // Get segment dimensions (in feet)
      const [segWidth, segHeight, segDepth] = getSegmentDimensions(
        edge.side,
        segmentLength,
        edge.height_ft,
        wallThickness
      )

      // Get segment offset from wall center (in feet)
      const [segOffsetX, segOffsetZ] = getSegmentOffset(
        edge.side,
        segment,
        edge.length_ft
      )

      // Calculate final wall position relative to unit center (in feet)
      const wallX = baseOffsetX + segOffsetX
      const wallY = segHeight / 2 // Center at half height
      const wallZ = baseOffsetZ + segOffsetZ

      // Convert to meters
      const wallPosMeters = new THREE.Vector3(
        wallX * FEET_TO_METERS,
        wallY * FEET_TO_METERS,
        wallZ * FEET_TO_METERS
      )

      // Create box geometry in meters
      const geometry = new THREE.BoxGeometry(
        segWidth * FEET_TO_METERS,
        segHeight * FEET_TO_METERS,
        segDepth * FEET_TO_METERS
      )

      // Build transformation matrix
      const transform = new THREE.Matrix4()

      if (hasRotation) {
        // First rotate around unit center
        transform.makeRotationY(rotationRad)

        // Apply rotation to wall position relative to unit center
        wallPosMeters.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationRad)
      }

      // Add unit position (already in meters)
      wallPosMeters.add(unitPos)
      transform.setPosition(wallPosMeters)

      // Bake transformation into geometry
      geometry.applyMatrix4(transform)

      // Compute BVH on final geometry
      geometry.computeBoundsTree()

      // Create mesh with identity matrix
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshBasicMaterial({ visible: false })
      )
      mesh.name = `unit-wall-${unitId}-${edge.side}-${segment.start_ft}`

      meshes.push(mesh)
      edgeSides.push(edge.side)
      isDoorFlags.push(false) // Solid wall, not a door

      // Create OBB for broad phase
      const positionAttribute = geometry.attributes.position as THREE.BufferAttribute
      const box = new THREE.Box3().setFromBufferAttribute(positionAttribute)
      const obb = new OBB().fromBox3(box)
      obbs.push(obb)

      debugLog(`Created wall segment: ${edge.side} [${segment.start_ft}-${segment.end_ft}]`, {
        segmentLength,
        wallPos: [wallX.toFixed(2), wallY.toFixed(2), wallZ.toFixed(2)],
      })
    }

    // Create door collision meshes for this edge
    const doorSegments = edge.doorSegments || []
    for (const doorSeg of doorSegments) {
      const doorWidth = doorSeg.end_ft - doorSeg.start_ft
      if (doorWidth <= 0) continue

      // Get door dimensions (in feet) - use door height, not wall height
      const [doorGeomWidth, doorGeomHeight, doorGeomDepth] = getSegmentDimensions(
        edge.side,
        doorWidth,
        doorSeg.height_ft,
        wallThickness
      )

      // Get door offset from wall center (in feet)
      // Door segment uses same offset logic as wall segment
      const doorSegmentAsWallSeg: WallSegment = { start_ft: doorSeg.start_ft, end_ft: doorSeg.end_ft }
      const [doorOffsetX, doorOffsetZ] = getSegmentOffset(
        edge.side,
        doorSegmentAsWallSeg,
        edge.length_ft
      )

      // Calculate final door position relative to unit center (in feet)
      const doorX = baseOffsetX + doorOffsetX
      const doorY = doorSeg.height_ft / 2 // Center at half door height
      const doorZ = baseOffsetZ + doorOffsetZ

      // Convert to meters
      const doorPosMeters = new THREE.Vector3(
        doorX * FEET_TO_METERS,
        doorY * FEET_TO_METERS,
        doorZ * FEET_TO_METERS
      )

      // Create box geometry in meters
      const doorGeometry = new THREE.BoxGeometry(
        doorGeomWidth * FEET_TO_METERS,
        doorGeomHeight * FEET_TO_METERS,
        doorGeomDepth * FEET_TO_METERS
      )

      // Build transformation matrix
      const doorTransform = new THREE.Matrix4()

      if (hasRotation) {
        // First rotate around unit center
        doorTransform.makeRotationY(rotationRad)
        // Apply rotation to door position relative to unit center
        doorPosMeters.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationRad)
      }

      // Add unit position (already in meters)
      doorPosMeters.add(unitPos)
      doorTransform.setPosition(doorPosMeters)

      // Bake transformation into geometry
      doorGeometry.applyMatrix4(doorTransform)

      // Compute BVH on final geometry
      doorGeometry.computeBoundsTree()

      // Create mesh with identity matrix
      const doorMesh = new THREE.Mesh(
        doorGeometry,
        new THREE.MeshBasicMaterial({ visible: false })
      )
      doorMesh.name = `unit-door-${unitId}-${edge.side}-${doorSeg.start_ft}`

      meshes.push(doorMesh)
      edgeSides.push(edge.side)
      isDoorFlags.push(true) // This is a door

      // Create OBB for broad phase
      const doorPosAttr = doorGeometry.attributes.position as THREE.BufferAttribute
      const doorBox = new THREE.Box3().setFromBufferAttribute(doorPosAttr)
      const doorObb = new OBB().fromBox3(doorBox)
      obbs.push(doorObb)

      debugLog(`Created door segment: ${edge.side} [${doorSeg.start_ft}-${doorSeg.end_ft}]`, {
        doorWidth,
        doorHeight: doorSeg.height_ft,
        doorType: doorSeg.doorType,
        doorPos: [doorX.toFixed(2), doorY.toFixed(2), doorZ.toFixed(2)],
      })
    }
  }

  debugLog(`Unit ${unitId}: Created ${meshes.length} wall/door segments (${isDoorFlags.filter(f => f).length} doors)`)

  return { obbs, meshes, unitId, areaId, edgeSides, isDoorFlags }
}

/**
 * Create wall collision meshes for multiple units
 */
export function createAllUnitWallMeshes(
  configs: UnitCollisionConfig[]
): Map<string, UnitWallCollisionResult> {
  const results = new Map<string, UnitWallCollisionResult>()

  for (const config of configs) {
    const result = createUnitWallMeshes(config)
    // Use unitId as key (unique within building)
    results.set(`${config.areaId}:${config.unitId}`, result)
  }

  debugLog(`Created wall meshes for ${configs.length} units`)

  return results
}

/**
 * Get aggregated OBBs and meshes from all units
 * Used for collision checking against all walls
 */
export function getAggregatedUnitWallCollision(
  unitWalls: Map<string, UnitWallCollisionResult>
): {
  allOBBs: OBB[]
  allMeshes: THREE.Mesh[]
  wallToUnitMap: Map<number, { unitId: string; areaId: string; side: 'front' | 'back' | 'left' | 'right'; isDoor: boolean }>
} {
  const allOBBs: OBB[] = []
  const allMeshes: THREE.Mesh[] = []
  const wallToUnitMap = new Map<
    number,
    { unitId: string; areaId: string; side: 'front' | 'back' | 'left' | 'right'; isDoor: boolean }
  >()

  for (const [, result] of unitWalls) {
    const startIndex = allOBBs.length

    for (let i = 0; i < result.obbs.length; i++) {
      wallToUnitMap.set(startIndex + i, {
        unitId: result.unitId,
        areaId: result.areaId,
        side: result.edgeSides[i],
        isDoor: result.isDoorFlags[i] || false,
      })
    }

    allOBBs.push(...result.obbs)
    allMeshes.push(...result.meshes)
  }

  return { allOBBs, allMeshes, wallToUnitMap }
}
